/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Bot,
  CalendarClock,
  CheckCircle2,
  Gauge,
  History,
  Layers,
  LineChart,
  Loader2,
  Mail,
  SearchCheck,
  Settings2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import PaxiaLogo from "../../branding/logo";
import homepageContent from "../../content/homepageContent";

const systemsSectionClass = "landing-section px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20";

const buildSteps = [
  {
    icon: Sparkles,
    title: "Start with the routine",
    body: "Pick the work you repeat: market scans, alerts, trade prep, journal review, or account checks.",
  },
  {
    icon: Settings2,
    title: "Add the inputs",
    body: "Choose the accounts, symbols, indicators, files, prompts, and research sources the routine needs.",
  },
  {
    icon: CalendarClock,
    title: "Run it your way",
    body: "Run it on demand, schedule it, or use it during selected trading sessions.",
  },
  {
    icon: History,
    title: "Improve from results",
    body: "Review outputs, actions, trade history, and costs before making changes.",
  },
];

const automationCards = [
  {
    icon: Layers,
    title: "Trading Workflows",
    body: "Create repeatable workflows for scans, alerts, trade prep, reviews, and account checks.",
  },
  {
    icon: Bot,
    title: "Bots",
    body: "Run clear strategy logic or monitoring tasks without rebuilding the setup each time.",
  },
  {
    icon: Sparkles,
    title: "AI Assistance",
    body: "Use AI for context-heavy steps like summarising research, checking conditions, or drafting a plan.",
  },
  {
    icon: Settings2,
    title: "Shared Inputs",
    body: "Reuse prompts, indicators, watchlists, files, and settings across workflows.",
  },
];

const workspaceCards = [
  {
    icon: SearchCheck,
    title: "Research",
    body: "Screen markets, create alerts, check economic calendar risk, review news, inspect insider activity, and run deeper analysis.",
  },
  {
    icon: LineChart,
    title: "Studio",
    body: "Build trades from one account, symbol, and timeframe with chart context, trade calculations, and supporting widgets.",
  },
  {
    icon: Gauge,
    title: "Dashboard",
    body: "Monitor balances, equity, open positions, pending orders, account widgets, workflows, and recent activity.",
  },
  {
    icon: BarChart3,
    title: "Review",
    body: "Use trade history, journals, source columns, equity curves, and performance stats to learn from completed trades.",
  },
];

const liveControls = [
  "Set max daily loss, max trade risk, max lot size, open-position limits, and cooldowns per account.",
  "Require manual approval before selected automated actions can be sent to a broker.",
  "Inspect run history, reliability, usage, cost, and the source behind each output.",
  "Review trade calculations before sending a chart-led order from Studio.",
];

export default function SystemsHomepage({ content = homepageContent }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const utm = useMemo(() => {
    if (typeof window === "undefined") {
      return { source: "", medium: "", campaign: "", content: "" };
    }

    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get("utm_source") || "",
      medium: params.get("utm_medium") || "",
      campaign: params.get("utm_campaign") || "",
      content: params.get("utm_content") || "homepage-systems",
    };
  }, []);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isEmailValid) {
      setError("Please enter a valid email.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, ...utm }),
      });

      if (!response.ok) {
        throw new Error("Submission failed. Try again.");
      }

      setMessage("Thanks - you're on the list.");
      setEmail("");
    } catch (submissionError) {
      setError(submissionError.message || "Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="landing-shell systems-homepage min-h-screen overflow-x-hidden">
      <SystemsHero content={content} />
      <BuildPathSection />
      <AutomationStackSection />
      <WorkspaceSection />
      <LiveTradingSection />
      <SystemsSignup
        content={content}
        email={email}
        error={error}
        isEmailValid={isEmailValid}
        loading={loading}
        message={message}
        onEmailChange={setEmail}
        onSubmit={handleSubmit}
      />
      <SystemsFooter content={content} />
    </main>
  );
}

function SystemsHero({ content }) {
  const { brand, hero } = content;

  return (
    <header className="landing-hero">
      <nav className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-3" aria-label={`${brand.name} home`}>
          <img src={PaxiaLogo} alt={brand.name} className="h-12 w-auto sm:h-[3.75rem]" />
        </a>

        <div className="hidden items-center gap-7 text-sm text-slate-300 md:flex">
          <a href="#build" className="transition-colors hover:text-[#00D5FF]">Build</a>
          <a href="#automation" className="transition-colors hover:text-[#00D5FF]">Automation</a>
          <a href="#workspace" className="transition-colors hover:text-[#00D5FF]">Workspace</a>
          <a href="#live-trading" className="transition-colors hover:text-[#00D5FF]">Live trading</a>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={hero.secondaryCta.href}
            className="hidden rounded-[8px] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white sm:inline-flex"
          >
            {hero.secondaryCta.label}
          </Link>
          <a
            href={hero.primaryCta.href}
            className="brand-button px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
          >
            {hero.primaryCta.label}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </nav>

      <section id="top" className="relative z-10 mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-10 sm:px-6 sm:pb-16 sm:pt-14 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:pb-20 lg:pt-16">
        <div className="flex flex-col justify-center">
          <p className="landing-eyebrow mb-5 px-3 py-1 text-xs font-semibold uppercase">
            Trading workflows for active traders
          </p>
          <h1 className="brand-text-gradient max-w-4xl text-5xl font-semibold leading-[1.02] sm:text-6xl lg:text-7xl">
            Build trading workflows around the way you trade.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
            Use Paxia to research markets, monitor broker accounts, plan trades in Studio, and automate the repeatable parts of your process with bots, AI, and scheduled workflows.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={hero.primaryCta.href}
              className="brand-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.primaryCta.label}
              <ArrowRight className="h-5 w-5" />
            </a>
            <Link
              to={hero.secondaryCta.href}
              className="secondary-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.secondaryCta.label}
            </Link>
          </div>

          <ul className="mt-8 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
            {[
              "Research, Studio, Dashboard, and Review",
              "Broker accounts and trading controls",
              "Bots, AI assistance, and scheduled workflows",
              "Run history, performance, and cost views",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <SystemBuilderPreview />
      </section>
    </header>
  );
}

function SystemBuilderPreview() {
  return (
    <div className="relative self-center">
      <div className="soft-panel p-2 sm:p-3">
        <div className="overflow-hidden rounded-[8px] border border-white/10 bg-[#070B13]/90">
          <div className="h-1 bg-[linear-gradient(90deg,#0B3DFF,#00D5FF,#6D5BFF)]" />
          <div className="border-b border-white/10 px-4 py-4">
            <p className="text-xs uppercase text-slate-500">Workflow</p>
            <div className="mt-1 flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-white">London session scanner</h2>
              <span className="status-pill rounded-[8px] border border-[#00D5FF]/25 bg-[#00D5FF]/10 px-3 py-1.5 text-xs font-semibold text-cyan-50">
                <span className="status-dot" />
                Active
              </span>
            </div>
          </div>

          <div className="grid gap-3 p-4 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="space-y-3">
              <PreviewPanel title="Build" icon={Sparkles}>
                <div className="space-y-2">
                  {[
                    ["Scanner", "Market conditions"],
                    ["Indicators", "EMA 50/100/200 + RSI"],
                    ["Research", "News + economic calendar"],
                    ["Output", "Watchlist brief + alert"],
                  ].map(([label, value]) => (
                    <PreviewRow key={label} label={label} value={value} />
                  ))}
                </div>
              </PreviewPanel>

              <PreviewPanel title="Run & Automate" icon={CalendarClock}>
                <div className="space-y-2">
                  <PreviewRow label="Mode" value="Scheduled weekdays" />
                  <PreviewRow label="Next run" value="07:30 local" />
                  <PreviewRow label="Action" value="Alert + draft plan" />
                </div>
              </PreviewPanel>
            </div>

            <div className="space-y-3">
              <PreviewPanel title="Monitor" icon={BarChart3}>
                <div className="chart-surface h-40 p-4">
                  <div className="flex h-full items-end gap-2">
                    {[52, 68, 44, 76, 62, 86, 58, 80, 72].map((height, index) => (
                      <div key={`${height}-${index}`} className="flex h-full flex-1 items-end">
                        <div
                          className={`chart-bar w-full rounded-sm ${index % 2 === 0 ? "bg-[#00D5FF]/80" : "bg-[#6D5BFF]/75"}`}
                          style={{ height: `${height}%`, "--bar-delay": `${index * 90}ms` }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <MiniStat label="Runs" value="24" />
                  <MiniStat label="Success" value="92%" />
                  <MiniStat label="Cost" value="Tracked" />
                </div>
              </PreviewPanel>

              <PreviewPanel title="Live trading context" icon={ShieldCheck}>
                <div className="space-y-2">
                  <PreviewRow label="Account" value="Demo review" />
                  <PreviewRow label="Risk state" value="Configured" />
                  <PreviewRow label="Approval" value="Required for live" />
                </div>
              </PreviewPanel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BuildPathSection() {
  return (
    <section id="build" className={`${systemsSectionClass} landing-section-panel`}>
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">From routine to workflow</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">Turn repeatable trading work into workflows.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            Start with a trading routine, add the tools it needs, choose when it runs, then review what happened before you refine it.
          </p>
          <div className="brand-divider mx-auto mt-6 max-w-sm" />
        </div>

        <ol className="workflow-rail grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {buildSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <li key={step.title} className="workflow-stage p-5">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div className="icon-tile">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-[#00D5FF]/60">{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-400">{step.body}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function AutomationStackSection() {
  return (
    <section id="automation" className={systemsSectionClass}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 max-w-3xl">
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">Automation</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">Automate the repeatable parts of your trading process.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            Use workflows, bots, AI assistance, and shared inputs to run common tasks without jumping between tools.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-4">
          {automationCards.map((card) => (
            <CapabilityCard key={card.title} {...card} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WorkspaceSection() {
  return (
    <section id="workspace" className={`${systemsSectionClass} landing-section-mesh`}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 max-w-3xl">
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">Trading workspace</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">Research, trade, and review from the same platform.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            Automation works best when it sits beside your normal trading work: account monitoring, market research, chart-led execution, and post-trade review.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {workspaceCards.map((card) => (
            <CapabilityCard key={card.title} {...card} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LiveTradingSection() {
  return (
    <section id="live-trading" className={`${systemsSectionClass} landing-section-wash`}>
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">Broker-connected trading</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">Keep account rules and trade evidence close to the workflow.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            When a workflow leads to a live trade, Paxia keeps the account, rule checks, approvals, run history, and trade evidence visible.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {liveControls.map((point) => (
            <div key={point} className="trust-point p-4 text-sm leading-6 text-slate-300">
              <CheckCircle2 className="mb-3 h-5 w-5 text-[#00D5FF]" />
              {point}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SystemsSignup({ content, email, error, isEmailValid, loading, message, onEmailChange, onSubmit }) {
  const { finalCta } = content;

  return (
    <section id={finalCta.id} className="landing-section landing-cta px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="cta-stage p-6 sm:p-8 lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr]">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-[#8BE9FF]">Beta access</p>
              <h2 className="max-w-3xl text-4xl font-semibold leading-[1.04] text-white sm:text-5xl">Build, run, and review trading workflows in one workspace.</h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                Join the beta if you want broker-connected trading, research tools, Studio, dashboards, bots, AI assistance, scheduled runs, and performance review in one platform.
              </p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {[
                  "Research, Studio, and Dashboard",
                  "Bots, AI assistance, and schedules",
                  "Run history, performance, and cost",
                  "Account risk rules for live trading",
                ].map((point) => (
                  <li key={point} className="cta-checkitem flex items-center gap-3 text-sm text-slate-100">
                    <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className="cta-form-shell p-5 sm:p-6" aria-live="polite">
              <div className="mb-5">
                <p className="text-sm font-semibold text-white">Request beta access</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">Tell us where to reach you. We will use this only for beta access updates.</p>
              </div>

              {message ? (
                <div className="cta-success p-6 text-center" role="status">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-[#00D5FF]" />
                  <p className="mt-3 text-lg font-semibold text-white">{message}</p>
                  <p className="mt-2 text-sm text-emerald-50">We received your submission and will be in touch.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid gap-3" aria-live="polite">
                  <label className="sr-only" htmlFor="systems-homepage-email">
                    Email
                  </label>
                  <input
                    id="systems-homepage-email"
                    type="email"
                    required
                    value={email}
                    onChange={(event) => onEmailChange(event.target.value)}
                    placeholder="you@domain.com"
                    className="min-h-12 rounded-[8px] border border-white/15 bg-[#05070D]/90 px-4 py-3 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-[#00D5FF] focus:ring-2 focus:ring-[#00D5FF]/20"
                  />
                  <button
                    type="submit"
                    disabled={loading || !isEmailValid}
                    className="brand-button min-h-12 px-5 py-3 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300 disabled:shadow-none"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                    {loading ? "Submitting..." : finalCta.primaryCta.label}
                  </button>
                </form>
              )}

              {error && <p className="mt-3 text-sm text-rose-200">{error}</p>}

              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-slate-400">
                <span>No financial advice. No guaranteed returns.</span>
                <Link to={finalCta.secondaryCta.href} className="text-cyan-100 transition-colors hover:text-white">
                  {finalCta.secondaryCta.label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SystemsFooter({ content }) {
  const { brand, footer } = content;

  return (
    <footer className="border-t border-white/10 bg-[#05070D] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 text-sm text-slate-400 md:flex-row md:items-start md:justify-between">
        <div className="max-w-2xl">
          <img src={PaxiaLogo} alt={brand.name} className="h-10 w-auto" />
          <p className="mt-4 leading-6">{footer.disclaimer}</p>
          <p className="mt-3">&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
        </div>
        <div className="flex flex-wrap gap-4 md:justify-end">
          {footer.links.map((link) =>
            link.href.startsWith("/") ? (
              <Link key={link.href} to={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </a>
            ),
          )}
          <a href={`mailto:${footer.contactEmail}`} className="transition-colors hover:text-[#00D5FF]">
            {footer.contactEmail}
          </a>
        </div>
      </div>
    </footer>
  );
}

function CapabilityCard({ icon: Icon, title, body }) {
  return (
    <article className="ai-capability p-5 sm:p-6">
      <div className="icon-tile mb-5 h-11 w-11 rounded-full">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="mt-3 text-sm leading-7 text-slate-300">{body}</p>
    </article>
  );
}

function PreviewPanel({ title, icon: Icon, children }) {
  return (
    <div className="landing-panel p-4">
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-300">
        <Icon className="h-4 w-4 text-[#00D5FF]" />
        {title}
      </div>
      {children}
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-[8px] border border-white/10 bg-white/[0.03] px-3 py-2">
      <span className="text-sm text-slate-400">{label}</span>
      <span className="text-right text-sm font-semibold text-cyan-100">{value}</span>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="metric-tile px-2 py-2">
      <div className="text-slate-500">{label}</div>
      <div className="mt-1 font-semibold text-white">{value}</div>
    </div>
  );
}
